import './TrevPabCalculator.css'
import { Helmet } from 'react-helmet-async';
import ArrowRight from '../../icons/ArrowRight';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import useLanguagePrefix from '../../LanguagePrefix';
import ToolFixedMenu from './ToolFixedMenu';


function TRevPABCalculator() {
    const langPrefix = useLanguagePrefix();
    const [totalRevenue, setTotalRevenue] = useState(null);
    const [bedAvailable, setBedAvailable] = useState(null);
    const [TRevPAB, setTRevPAB] = useState(null);
    const { t } = useTranslation();


    const handleInputChange = (e, type) => {
        const value = e.target.value;
        if (type === 'revenue') {
            setTotalRevenue(value);
        } else if (type === 'available') {
            setBedAvailable(value);
        }
    };


    const calculateTRevPAB = () => {
        if (totalRevenue > 0 && bedAvailable > 0) {
            const result = totalRevenue / bedAvailable;
            const formatted = Number.isInteger(result) ? result : result.toFixed(2);
            setTRevPAB(formatted);
        } else {
            setTRevPAB(null);
        }
    };

    return (
        <div>
            <Helmet> 

                <title>TRevPAB Calculator – Total Revenue Per Available Bed - Prostay</title>
                <meta name="description" content="Use Prostay’s TRevPAB calculator to measure total revenue per available bed. Ideal for hostels and shared accommodations focused on maximizing income and efficiency." />
                <link rel="canonical" href={"https://www.prostay.com" + langPrefix + "/trevpab-calculator/"} />

                 <link rel="alternate" href="https://www.prostay.com/es/trevpab-calculator/" />
                 <link rel="alternate" href="https://www.prostay.com/en/trevpab-calculator/" />

                <meta property="og:title" content="TRevPAB Calculator – Total Revenue Per Available Bed - Prostay" />
                <meta property="og:description" content="Use Prostay’s TRevPAB calculator to measure total revenue per available bed. Ideal for hostels and shared accommodations focused on maximizing income and efficiency." />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={"https://www.prostay.com" + langPrefix + "/trevpab-calculator/"} />
                <meta property="og:image" content='https://prostay.com/images/ogImage.png' />

                <meta property="og:site_name" content="Prostay" />

                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="TRevPAB Calculator – Total Revenue Per Available Bed - Prostay" />
                <meta name="twitter:description" content="Use Prostay’s TRevPAB calculator to measure total revenue per available bed. Ideal for hostels and shared accommodations focused on maximizing income and efficiency." />
                <meta name="twitter:image" content='https://prostay.com/images/ogImage.png' />
                <meta name="twitter:site" content="@prostaypms" />
                <meta name="twitter:creator" content="@prostaypms" />

            </Helmet>
            <div className='d-flex trevpab-calculator flex-column-reverse flex-xl-row'>
                <div className='d-flex flex-column width-left gap-4'>
                    <div>
                        <h1 style={{ margin: '0px' }}>
                            {t('TRevPAB Calculator - Total Revenue Per Available Bed')}
                        </h1>
                        <h6 className='mt-4'>
                            {t('Track Total Revenue Per Available Bed.')} <br />
                            {t('Use this free TRevPAB calculator to measure how much revenue your property generates for each available bed — ideal for hostels, dorms, and shared accommodation setups.')}
                        </h6>
                        <div className='d-flex flex-column-reverse flex-xl-row gap-2 gap-sm-5 mt-5'>
                            <div className='d-flex flex-column gap-4'>
                                <div className='d-flex flex-column mt-4'>
                                    <label>
                                        {t('Total Revenue')}
                                    </label>
                                    <input
                                        type='number'
                                        value={totalRevenue}
                                        onChange={(e) => handleInputChange(e, 'revenue')}
                                        placeholder={t('Enter amount here')} />
                                </div>
                                <div className='d-flex flex-column mt-4'>
                                    <label>
                                        {t('Total Available Beds')}
                                    </label>
                                    <input
                                        type='number'
                                        value={bedAvailable}
                                        onChange={(e) => handleInputChange(e, 'available')}
                                        placeholder={t('Enter number here')} />
                                </div>
                                <div className='d-flex gap-3 gap-sm-5 flex-column flex-sm-row align-items-center'>
                                    <button className='light-blue-filled-btn-xsmall' style={{ height: '38px', width: '111px' }} onClick={calculateTRevPAB}>
                                        {t('Calculate')}
                                        <ArrowRight />
                                    </button>
                                    <div className='d-flex flex-column w-100'>
                                        <label>
                                            {t('TRevPAB')}
                                        </label>
                                        <input
                                            readOnly
                                            value={TRevPAB}
                                            type='number'
                                            placeholder='0' />
                                    </div>
                                </div>
                            </div>
                            <div className='center-div'>
                                <h2>
                                    {t('What is TRevPAB?')}
                                </h2>
                                <p className='mt-3'>
                                    {t('TRevPAB, or Total Revenue Per Available Bed, is a performance metric used primarily by hostels, shared accommodations, and multi-bed room properties. It measures how much total revenue your property earns per bed available, offering a more accurate view of performance than traditional room-based metrics like RevPAR or TRevPAR.')}
                                </p>
                                <br />
                                <p>
                                    {t('This is especially important for properties where one room might accommodate four, six, or even more guests. By focusing on revenue per bed, managers can better analyze pricing, demand, and profitability in line with their actual inventory and guest capacity.')}
                                </p>
                                <br />
                                <p>
                                    {t('TRevPAB includes revenue from all sources — not just beds sold, but also food and beverage, tours, and other services.')}
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className='gap-3 d-flex flex-column mt-3'>
                        <h2>
                            {t('TRevPAB Formula')}
                        </h2>
                        <p>
                            {t('The formula for calculating TRevPAB is straightforward:')}
                        </p>
                        <div className='d-flex align-items-center formula gap-2'>
                            <p>
                                {t('TRevPAB')}  =
                            </p>
                            <div className='d-flex flex-column align-items-center'>
                                <p>
                                    {t('Total Revenue')}
                                </p>
                                <p style={{ borderTop: '0.5px solid #1643E0' }}>
                                    {t('Total Available Beds')}
                                </p>
                            </div>
                        </div>
                        <p>
                            {t('To calculate this, take your property’s total revenue over a given period — including accommodation and non-room sales — and divide it by the total number of beds available.')}
                        </p>
                        <p>
                            {t('For example, if you earned $90,000 over a 30-day month and had 100 beds available, you would divide $90,000 by 3,000 available bed-nights (100 beds × 30 days). The result tells you how much revenue each available bed contributed to the business.')}
                        </p>
                    </div>
                    <div className='d-flex flex-column gap-3'>
                        <h2>
                            {t('Why TRevPAB is Critical for Shared Accommodations')}
                        </h2>
                        <p>
                            {t('For traditional hotels, metrics like RevPAR or GOPPAR work well because rooms are the primary inventory unit. But for shared accommodations, where guests book beds instead of entire rooms, those metrics don’t accurately reflect demand or revenue efficiency.')}
                        </p>
                        <p>
                            {t('TRevPAB gives operators a more granular and relevant performance view by aligning revenue tracking with actual guest capacity. By monitoring TRevPAB, managers can identify how well different bed types or dorm configurations are performing, optimize pricing across seasons, and assess the impact of upsells like breakfast or tours.')}
                        </p>
                        <p>
                            {t('This data also helps with budgeting, staff planning, and marketing — all tuned to per-bed performance rather than per-room generalizations.')}
                        </p>
                    </div>
                    <div className='d-flex flex-column gap-3 mt-2'>
                        <h2>
                            {t('TRevPAB vs RevPAR or TRevPAR')}
                        </h2>
                        <p>
                            {t('TRevPAB is designed for properties that don’t operate on a one-room-per-guest basis. RevPAR measures revenue per room, and TRevPAR expands that to include all revenue sources, but both still assume a fixed room count.')}
                        </p>
                        <p>
                            {t('TRevPAB shifts the focus to beds, allowing for more accurate performance analysis in dormitory-style or shared spaces. This metric is particularly helpful for comparing dorms with different bed capacities, evaluating seasonal demand, and setting tiered pricing structures per bed.')}
                        </p>
                        <p>
                            {t('For operators running a hybrid of private rooms and shared dorms, using TRevPAB alongside RevPAR gives a complete performance view across inventory types.')}
                        </p>
                    </div>
                    <div className='d-flex flex-column gap-3 mt-2'>
                        <h2>
                            {t('Get More Out of Every Bed with Prostay')}
                        </h2>
                        <p>
                            {t('Prostay empowers hostels and shared accommodations with the tools they need to track performance, optimize pricing, and grow revenue — bed by bed.')}
                        </p>
                        <p>
                            {t('From smart rate rules to integrated POS and financial reporting, Prostay gives you control and clarity over every revenue stream in your business.')}
                        </p>
                        <p>
                            {t('With TRevPAB insights built directly into your dashboards, you’ll always know how much value each bed is delivering — and how to increase it.')}
                        </p>
                    </div>
                </div>
               <ToolFixedMenu/>
            </div>
        </div>
    )
}

export default TRevPABCalculator;